import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  alpha,
  useTheme
} from '@mui/material'
import {
  Dashboard as DashboardIcon,
  People,
  Assignment,
  TrendingUp,
  AccessTime,
  Email,
  Settings,
  PersonAdd,
  Business,
  CheckCircle,
  Schedule
} from '@mui/icons-material'
import { useAuth } from '@shared/auth/AuthContext'
import { colors } from '@shared/styles/theme'
import { AnalyticsService, AdminDashboardStats, RecentActivity } from '@shared/services/ApiClient'

function AdminDashboard() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const theme = useTheme()
  const [apiStatus, setApiStatus] = useState<'loading' | 'success' | 'error' | 'idle'>('idle')
  const [adminStats, setAdminStats] = useState<AdminDashboardStats>({
    totalTickets: 0,
    openTickets: 0,
    resolvedToday: 0,
    responseTime: '0h',
    totalUsers: 0,
    activeAgents: 0,
    totalOrganizations: 0,
    setupComplete: false
  })
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([])

  useEffect(() => {
    loadAdminStats()
    loadRecentActivity()
  }, [])

  const loadAdminStats = async () => {
    setApiStatus('loading')
    try {
      const stats = await AnalyticsService.getAdminDashboard()
      setAdminStats(stats)
      setApiStatus('success')
    } catch (error) {
      console.error('Failed to load admin stats:', error)
      setApiStatus('error')
    }
  }

  const loadRecentActivity = async () => {
    try {
      const activity = await AnalyticsService.getRecentActivity()
      setRecentActivity(activity)
    } catch (error) {
      console.error('Failed to load recent activity:', error)
      setRecentActivity([])
    }
  }

  const getQuickActions = () => {
    // Don't show quick actions for oversight users - they only need to view/manage tickets
    if (user?.role === 'Oversight') {
      return []
    }

    const allActions = [
      {
        title: 'Invite Team Member',
        description: 'Add a new agent or admin to your team',
        icon: <PersonAdd />,
        color: theme.palette.primary.main,
        action: () => window.location.href = '/admin/users?action=invite',
        roles: ['Admin']
      },
      {
        title: 'Manage Organisations',
        description: 'Create and manage organisation structure',
        icon: <Business />,
        color: theme.palette.secondary.main,
        action: () => window.location.href = '/admin/organizations',
        roles: ['Admin']
      },
      {
        title: 'Configure Email',
        description: 'Set up email integration and templates',
        icon: <Email />,
        color: colors.orange,
        action: () => window.location.href = '/admin/settings?tab=email',
        roles: ['Admin']
      },
      {
        title: 'System Settings',
        description: 'Configure helpdesk preferences',
        icon: <Settings />,
        color: colors.techBlue,
        action: () => window.location.href = '/admin/settings',
        roles: ['Admin']
      }
    ]

    // Filter actions based on user role
    return allActions.filter(action => action.roles.includes(user?.role || 'Customer'))
  }

  const getActivityIcon = (type: string) => {
    if (type.toLowerCase().includes('user') || type.toLowerCase().includes('invitation')) return <PersonAdd />
    if (type.toLowerCase().includes('ticket')) return <Assignment />
    if (type.toLowerCase().includes('organization') || type.toLowerCase().includes('organisation')) return <Business />
    return <CheckCircle />
  }

  const getActivityColor = (type: string) => {
    if (type.toLowerCase().includes('user') || type.toLowerCase().includes('invitation')) return theme.palette.success.main
    if (type.toLowerCase().includes('ticket')) return theme.palette.primary.main
    if (type.toLowerCase().includes('organization') || type.toLowerCase().includes('organisation')) return theme.palette.info.main
    return theme.palette.text.secondary
  }


  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <DashboardIcon sx={{ fontSize: 32, color: theme.palette.primary.main }} />
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Admin Dashboard
        </Typography>
      </Box>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Welcome back, {user?.firstName || 'Admin'}! Here's your helpdesk overview and management tools.
      </Typography>

      {/* Key Statistics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Assignment sx={{ fontSize: 40, color: colors.blue, mb: 1 }} />
              <Typography variant="h4">
                {apiStatus === 'loading' ? <CircularProgress size={24} /> : adminStats.totalTickets}
              </Typography>
              <Typography color="text.secondary">Total Tickets</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {adminStats.openTickets} open
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <AccessTime sx={{ fontSize: 40, color: colors.orange, mb: 1 }} />
              <Typography variant="h4">
                {apiStatus === 'loading' ? <CircularProgress size={24} /> : adminStats.responseTime}
              </Typography>
              <Typography color="text.secondary">Avg Response Time</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {adminStats.resolvedToday} resolved today
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <People sx={{ fontSize: 40, color: colors.techBlue, mb: 1 }} />
              <Typography variant="h4">
                {apiStatus === 'loading' ? <CircularProgress size={24} /> : adminStats.totalUsers}
              </Typography>
              <Typography color="text.secondary">Team Members</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {adminStats.activeAgents} active agents
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Business sx={{ fontSize: 40, color: colors.greenDark, mb: 1 }} />
              <Typography variant="h4">
                {apiStatus === 'loading' ? <CircularProgress size={24} /> : adminStats.totalOrganizations}
              </Typography>
              <Typography color="text.secondary">Organisations</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Multi-tenant setup
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Quick Actions */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUp />
                Quick Actions
              </Typography>
              <Grid container spacing={2}>
                {getQuickActions().map((action, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Card
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: theme.shadows[4]
                        },
                        background: alpha(action.color, 0.05),
                        border: `1px solid ${alpha(action.color, 0.2)}`
                      }}
                      onClick={action.action}
                    >
                      <CardContent sx={{ p: 2, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, height: '100%' }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: '50%',
                              bgcolor: action.color,
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0
                            }}
                          >
                            {action.icon}
                          </Box>
                          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', minHeight: '28px' }}>
                              {action.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                              {action.description}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                <Schedule />
                Recent Activity
              </Typography>
              <List dense>
                {recentActivity.map((activity) => (
                  <ListItem key={activity.id} sx={{ px: 0, py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          bgcolor: alpha(getActivityColor(activity.type), 0.1),
                          color: getActivityColor(activity.type),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {getActivityIcon(activity.type)}
                      </Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={activity.message}
                      secondary={activity.timestamp}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))}
              </List>
              <Button
                variant="text"
                size="small"
                sx={{ mt: 1 }}
                onClick={() => navigate('/admin/tickets')}
              >
                View All Activity
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* API Status */}
      {apiStatus === 'error' && (
        <Alert severity="error" sx={{ mt: 3 }}>
          Unable to load dashboard statistics. Some features may be unavailable.
        </Alert>
      )}
    </Box>
  )
}

export default AdminDashboard